<template>
    <div class="calendar">
      <div class="header">
        <button class="rounded-btn" type="button" @click="previousMonth">&lt;</button>
        <h2>{{ selectedMonthName }}</h2>
        <button class="rounded-btn" type="button" @click="nextMonth">&gt;</button>
      </div>
      <div class="body">
        <div class="weekdays">
            <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
        </div>
            <div class="dates">
                <div  v-for="(date, index) in calendarDates" :key="index" :class="dateClass(date)" @click="!date.disabled ? selectDate(date.date) : null">
                    <div :class="{
                      'inner': !date.disabled,
                      'inner-disabled': date.disabled}" >
                    <p>{{ date.day }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format } from 'date-fns';
export default {
    props: {
        closedDays: { type: Array },
        minDate: { type: Date },
        maxDate: { type: Date },
        setDate: { type: Function },
        setSearchDate: { type: Function },
        getOpeningTimes: { type: Function },
    },
    data() {
        return {
            currentMonthIndex: this.minDate.getMonth(),
            weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            selectedDate: this.minDate,
            year: null,
            chosenDate: null
        };
    },

    computed: {
        formattedClosedDays() {
            const formattedDates = this.closedDays.map(date => new Date(date).toISOString().slice(0, 10));
            const uniqueDays = Array.from(new Set(formattedDates));
            return uniqueDays.map(date => new Date(date));
        },
        dateClass() {
            return (date) => {
                return {
                    'date-selected': date.date === this.chosenDate,
                    'date': !date.disabled,
                    'date-disabled': date.disabled,
                };
            };
        },
        selectedMonthName() {
            return this.selectedMonth.toLocaleString('default', { month: 'long' });
        },
        selectedMonth() {
            return this.selectedDate
        },
        calendarDates() {
            const year = this.selectedDate.getFullYear();
            const month = this.selectedDate.getMonth();

            const numDaysInMonth = new Date(year, month + 1, 0).getDate();
            const firstDayOfMonth = new Date(year, month, 1).getDay();
            const dates = [];

            // Determine the last day of the previous month
            const lastDayOfPreviousMonth = new Date(year, month, 0).getDate();
            // Add dates from the previous month
            for (let i = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; i > 0; i--) {
                dates.push({
                    day: lastDayOfPreviousMonth - i + 1,
                    disabled: true
                });
            }
            // Add dates from the current month
            for (let i = 1; i <= numDaysInMonth; i++) {
                const date = new Date(year, month, i);
                date.setHours(0, 0, 0, 0)
                dates.push({
                    date: date,
                    day: i,
                    disabled: this.isDateDisabled(date)
                });
            }
            // Add dates from the next month to complete the week
            const lastDayOfWeek = new Date(year, month, numDaysInMonth).getDay();
            const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
            for (let i = 1; i <= daysToAdd; i++) {
                dates.push({
                    day: i,
                    disabled: true
                });
            }
            return dates;
        },
    },

    methods: {
        checkAndAdvanceMonth() {
            const allDatesDisabled = this.calendarDates.every(date => date.disabled);
            if (allDatesDisabled) {
                this.nextMonth();
                this.checkAndAdvanceMonth();
            }
        },
        isDateDisabled(date) {
            const dateString = date.toISOString().slice(0, 10);
            // Check if the formatted date string is included in the array of closed days
            const isIncluded = this.formattedClosedDays.some(closedDate => {
                return closedDate.toISOString().slice(0, 10) === dateString;
            });
            // Check if the date is before minDate or after maxDate
            const isBeforeMinDate = this.minDate && date <= this.minDate;
            const isAfterMaxDate = this.maxDate && date > this.maxDate;
            // Return true if the date is included in closed days or before minDate or after maxDate
            return isIncluded || isBeforeMinDate || isAfterMaxDate;
        },
        nextMonth() {
            const updatedDate = new Date(this.selectedDate);
            const originalDate = new Date(updatedDate);

            // Temporarily set the date to the next month and don't show next month if no dates
            updatedDate.setDate(1);
            updatedDate.setMonth(originalDate.getMonth() + 1);
            const year = updatedDate.getFullYear();
            const month = updatedDate.getMonth();
            const numDaysInMonth = new Date(year, month + 1, 0).getDate();
            let hasEnabledDate = false;
            for (let i = 1; i <= numDaysInMonth; i++) {
                const date = new Date(year, month, i);
                if (!this.isDateDisabled(date)) {
                    hasEnabledDate = true;
                    break;
                }
            }

            if (hasEnabledDate && originalDate <= this.maxDate) {
                const dayOfMonth = this.selectedDate.getDate();
                const daysInNewMonth = new Date(updatedDate.getFullYear(), updatedDate.getMonth() + 1, 0).getDate();
                updatedDate.setDate(Math.min(dayOfMonth, daysInNewMonth));
                this.currentMonthIndex++;
                this.selectedDate = updatedDate;
            }
        },
        previousMonth() {
            const updatedDate = new Date(this.selectedDate);
            updatedDate.setMonth(updatedDate.getMonth() - 1);
            const minDateStart = new Date(this.minDate);
            minDateStart.setDate(1);
            if (updatedDate >= minDateStart) {
                this.currentMonthIndex--;
                this.selectedDate = updatedDate;
            }
        },
        selectDate(date) {
            this.chosenDate = date;
            const searchDate = format(this.chosenDate, "YYYY-MM-DD");
            const formattedDate = this.formatSelectedDate(date);

            this.handleSetDate(formattedDate);
            this.handleSearchDate(searchDate);
            this.handleGetOpeningTimes();

        },
        formatSelectedDate(date) {
            const day = date.getDate();
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            const suffix = this.getDaySuffix(day);
            return `${day}${suffix} ${month} ${year}`;
        },
        getDaySuffix(day) {
            if (day >= 11 && day <= 13) {
                return 'th';
            }
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        },

        handleSetDate(formattedDate) {
            if (this.setDate) {
                this.setDate(formattedDate);
            } else {
                this.$store.commit("setDate", formattedDate);
            }
        },

        handleSearchDate(searchDate) {
            if (this.setSearchDate) {
                this.setSearchDate(searchDate);
            } else {
                this.$store.commit("setSearchDate", searchDate);
            }
        },

        handleGetOpeningTimes() {
            if (this.getOpeningTimes) {
                this.getOpeningTimes();
            } else {
                this.$store.dispatch("getOpeningTimes");
            }
        }
    },

    mounted() {
        // If no dates available go to next month
        this.checkAndAdvanceMonth();
    }
};
</script>

<style scoped lang="scss">
  .calendar {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }
  .body{
    padding: 2rem 1rem;
    background-color: #E5F4FA;
    border-radius: 5px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .weekdays {
    display: flex;
  }

  .weekday {
    flex: 1;
    opacity: 1;
    color: rgba(25, 42, 68, 1);
    font-size: 12px;
    font-weight: 500;
    font-style: Medium;
    letter-spacing: 0px;
    text-align: center;
  }

  .dates {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .date {
    padding: 0.25rem;

    cursor: pointer;
    .inner{
      display: flex;
      justify-content: center;
      align-items: center;
      height: 44px;
      border-radius: 4px;
      border: 1px solid #00A3E0;
    }
    .inner:hover {
        background-color: #00A3E0;
        p{
            color: #FFFFFF;
        }
    }
  }
  .date-disabled {
    padding: 0.25rem;
    opacity: 50%;
    cursor: not-allowed;
    .inner-disabled{
      display: flex;
      justify-content: center;
      align-items: center;
      height: 44px;
      background-color: #FFFFFF;
      border-radius: 4px;
      border: 1px solid #E5F4FA;
        p{
        opacity: 37%;
        color: #00A3E0;
        font-weight: 400;
        font-size: 16px;
        opacity: 0.37;
      }
    }
    }
    .date-selected{
      .inner {
        background-color: #00A3E0 !important;
        p{
            color: #FFFFFF !important;
        }
      }
    }

  p{
    opacity: 1;
    color: rgba(0, 163, 224, 1);
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0px;
    text-align: left;
  }
  h2{
    opacity: 1;
    color: rgba(0, 163, 224, 1);
    font-size: 16px;
    font-weight: 700;
    font-style: Bold;
    letter-spacing: 0px;
    text-align: left;
  }
  .rounded-btn{
    display:flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    width: 45px;
    height:45px;
    border-radius: 10rem;
    background: none;
    border: 2px solid rgba(0, 163, 224, 1);
    opacity: 1;
    color: #00A3E0;
    &:hover{
      box-shadow: 0px 0px 8px rgba(103, 205, 174, 1);
      cursor: pointer;
    }
  }
</style>